// src\components\ChatGPT4\ChatGPT4.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { OPENAI_API_URL } from '../../utils/constants';
import { useAppDispatch, useAppState } from '../../context/AppContext';
import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import ChatOutput from './ChatOutput';

const ChatGPT4 = ({
  setMarkdownText,
  markdownText,
  updateTokenCounts,
  modelSettings,
}) => {
  const dispatch = useAppDispatch();
  const { chatLogs, isStreamingEnabled } = useAppState();

  const username = 'mockUser123'; // Placeholder for actual username

  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [inputTokens, setInputTokens] = useState(0);
  const [outputTokens, setOutputTokens] = useState(0);

  const serverUrl = process.env.REACT_APP_BACKEND_URL || 'https://chatlogbackend-41d6c3e460b1.herokuapp.com';

  const handleClearOutput = () => {
    setOutput('');
  };

  const saveChatLog = async (chatLog) => {
    try {
      const response = await axios.post(`${serverUrl}/saveChatLog`, chatLog);
      if (response.data.success) {
        console.log('Chat log saved successfully - ChatGPT4.js');
      } else {
        throw new Error('Failed to save chat log - ChatGPT4.js');
      }
    } catch (error) {
      console.error('ChatGPT4.js - Error saving chat log:', error);
      throw error;
    }
  };

  const onMessageReceived = async (newMessage, openAIResponseData) => {
    const newLog = {
      username,
      message: newMessage,
      prompt: input,
      profile_timestamp: new Date().toISOString(),
      openAIResponse: openAIResponseData,
    };
    try {
      await saveChatLog(newLog);
      dispatch({ type: 'SET_CHAT_LOGS', payload: [...chatLogs, newLog] });
    } catch (error) {
      console.error('ChatGPT4.js - Error saving chat log:', error);
    }
  };

  const fetchResponse = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const payload = {
        model: modelSettings.modelVersion,
        temperature: modelSettings.temperature,
        max_tokens: modelSettings.maxTokens,
        messages: [{ role: 'user', content: input }],
      };
      const response = await axios.post(OPENAI_API_URL, payload, {
        headers: { Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}` },
      });
      const generatedMessage = response.data.choices[0].message.content;
      setOutput(generatedMessage);
      onMessageReceived(generatedMessage, response.data);
      const actualTokensUsed = response.data.usage.total_tokens;
      updateTokenCounts(actualTokensUsed, 1);
    } catch (err) {
      setApiError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchResponseWithStream = async () => {
    // Your streaming code here
  };

  const fetchResponseOrStream = async () => {
    if (isStreamingEnabled) {
      fetchResponseWithStream();
    } else {
      fetchResponse();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output).then(
      () => {
        console.log('Copied to clipboard');
      },
      (err) => {
        console.error('Failed to copy text:', err);
      },
    );
  };

  const handleSend = () => {
    const newText = markdownText ? `${markdownText}\n${output}` : output;
    setMarkdownText(newText);
    const updatedModelSettings = {
      ...modelSettings,
      prompt: input,
    };
    sessionStorage.setItem('modelSettings', JSON.stringify(updatedModelSettings));
  };

  // Effect to calculate input tokens
  useEffect(() => {
    const tokens = input.split(' ').length;
    setInputTokens(tokens);
  }, [input]);

  // Effect to calculate output tokens
  useEffect(() => {
    const tokens = output.split(' ').length;
    setOutputTokens(tokens);
  }, [output]);

  return (
    <div>
      <ChatHeader inputTokens={inputTokens} outputTokens={outputTokens} />
      <ChatInput
        input={input}
        setInput={setInput}
        fetchResponseOrStream={fetchResponseOrStream}
        loading={loading}
      />
      <ChatOutput
        output={output}
        copyToClipboard={copyToClipboard}
        handleSend={handleSend}
        handleClear={handleClearOutput} // Passing the clear output function
      />
      {loading && <p style={{ color: 'red', fontWeight: 'bold' }}>Loading...</p>}
      {apiError && (
        <p>
          <span>Error: </span>
          {apiError}
        </p>
      )}
    </div>
  );
};

ChatGPT4.propTypes = {
  setMarkdownText: PropTypes.func.isRequired,
  markdownText: PropTypes.string.isRequired,
  updateTokenCounts: PropTypes.func.isRequired,
  modelSettings: PropTypes.shape({
    modelVersion: PropTypes.string,
    temperature: PropTypes.number,
    maxTokens: PropTypes.number,
    prompt: PropTypes.string,
    role: PropTypes.string,
    topP: PropTypes.number,
    frequencyPenalty: PropTypes.number,
    presencePenalty: PropTypes.number,
    stopSequences: PropTypes.arrayOf(PropTypes.string),
    echo: PropTypes.bool,
    stream: PropTypes.bool,
    bestOf: PropTypes.number,
  }).isRequired,
};

export default ChatGPT4;
